import { logger } from "#webapp/src/util/logger.util";
import { RecurrenceRule } from "@clockwise/client-commons/src/datatypes/RecurrenceRule";
import {
  ConferencingType,
  FlexDetailsInput,
  ProposalState,
  TradeoffType,
} from "@clockwise/schema/v2";
import {
  useGatewayMutation,
  useGatewayQuery,
} from "@clockwise/web-commons/src/network/apollo/gateway-provider";
import { getRenderTimeZone } from "@clockwise/web-commons/src/util/time-zone.util";
import { DateTime, Interval } from "luxon";
import React, {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import invariant from "tiny-invariant";
import { useDebounceValue } from "usehooks-ts";
import {
  CreatePersistedProposalDocument,
  CreatePersistedProposalFromEventDocument,
  CurrentPersistedProposalDocument,
  UpdatePersistedProposalDocument,
} from "./apollo/__generated__/CreateProposal.v2.generated";
import { Attendee, EventPermissions } from "./CurrentProposalContext";

type Props = {
  eventId?: string | null;
  proposalId: string | null;
  title: string;
  startTime: DateTime;
  endTime: DateTime;
  timeZone: string;
  allDay: boolean;
  initialAttendeeIds: string[];
  attendees: Attendee[];
  removedAttendees: Record<string, Attendee>;
  upsertedAttendees: Record<string, Attendee>;
  description: string;
  recurrenceRule: RecurrenceRule | null;
  conferenceType: ConferencingType | null;
  location: string | null;
  initialized: boolean;
  flexDetails: FlexDetailsInput;
  permissions?: EventPermissions;
  meta: {
    isDragging: boolean;
  };
};

type AffectedAttendee = {
  person: {
    isMe: boolean;
    displayName: string;
    externalImageUrl: string | null;
    email: string;
  };
};

type TradeoffAttendee = {
  person: {
    isMe: boolean;
    email: string;
    givenName: string | null;
    familyName: string | null;
    externalImageUrl: string | null;
  };
};

type GQLTime =
  | {
      __typename: "DateRange";
      dateRange: string;
    }
  | {
      __typename: "DateTimeRange";
      dateTimeRange: string;
    }
  | null;

type Tradeoff = {
  title: string;
  attendees: TradeoffAttendee[];
  event: {
    externalEventId: string;
    title: string;
    dateOrTimeRange: GQLTime;
  } | null;
  updatedTime?: GQLTime;
  diffId?: string | null;
};

export type TradeoffBlock = {
  tradeoffs: Tradeoff[];
  affectedAttendees: AffectedAttendee[];
  tradeoffType: TradeoffType;
};

type PersistedProposalState = {
  id: string | null;
  status: ProposalState;
  tradeoffBlocks?: TradeoffBlock[];
};

type PersistedProposalWriteObj = {
  clearPersistedProposal: () => void;
};

const ReadContext = createContext<PersistedProposalState>({
  id: null,
  status: ProposalState.LoadingSuggestions,
  tradeoffBlocks: undefined,
});

const WriteContext = createContext<PersistedProposalWriteObj | null>(null);

export const PersistedProposalProvider = ({
  eventId,
  proposalId,
  description: inputDescription,
  title: inputTitle,
  location: inputLocation,
  attendees: inputAttendees,
  startTime,
  endTime,
  recurrenceRule,
  conferenceType,
  flexDetails,
  permissions,
  meta,
  children,
}: PropsWithChildren<Props>) => {
  const [pollInterval, setPollInterval] = useState(0);
  const [description, setDescription] = useDebounceValue(inputDescription, 200);
  const [title, setTitle] = useDebounceValue(inputTitle, 200);
  const [location, setLocation] = useDebounceValue(inputLocation, 200);

  const [localProposalId, setLocalProposalId] = useState(proposalId);
  const [proposalStatus, setProposalStatus] = useState(ProposalState.LoadingSuggestions);
  const [tradeoffBlocks, setTradeoffBlocks] = useState<TradeoffBlock[] | undefined>(undefined);

  // If we're creating a proposal for a new event, we can always modify it
  const canModify = eventId ? permissions?.canModify ?? false : true;

  useEffect(() => {
    // Update the description, but with a debounce
    setDescription(inputDescription);
  }, [inputDescription, setDescription]);

  useEffect(() => {
    // Update the title, but with a debounce
    setTitle(inputTitle);
  }, [inputTitle, setTitle]);

  useEffect(() => {
    // Update the location, but with a debounce
    setLocation(inputLocation);
  }, [inputLocation, setLocation]);

  const [
    createProposal,
    { loading: createProposalLoading, error: createProposalError },
  ] = useGatewayMutation(CreatePersistedProposalDocument, {
    onCompleted(data) {
      if (data.createProposal?.id) {
        setLocalProposalId(data.createProposal.id);
        setProposalStatus(data.createProposal.state);
      }
    },
  });

  const [
    createProposalFromEvent,
    { loading: createProposalFromEventLoading, error: createProposalFromEventError },
  ] = useGatewayMutation(CreatePersistedProposalFromEventDocument, {
    onCompleted(data) {
      if (data.createProposalFromEvent?.id) {
        setLocalProposalId(data.createProposalFromEvent.id);
        setProposalStatus(data.createProposalFromEvent.state);
      }
    },
  });

  const [updateProposal] = useGatewayMutation(UpdatePersistedProposalDocument, {
    onCompleted(data) {
      if (data.updateProposal?.id) {
        const diff = data?.updateProposal?.diffBlocks[0]?.diffs[0];
        const tradeoffBlocks = diff?.__typename !== "OthersDiff" ? diff.tradeoffBlocks : undefined;
        setProposalStatus(data.updateProposal.state);
        setTradeoffBlocks(tradeoffBlocks);
      }
    },
  });

  const { data } = useGatewayQuery(CurrentPersistedProposalDocument, {
    variables: { id: localProposalId! },
    skip: !localProposalId,
    pollInterval,
  });

  const proposalState = data?.proposal?.state;

  useEffect(() => {
    if (data?.proposal?.id && data.proposal?.state !== ProposalState.LoadingSuggestions) {
      const diff = data.proposal.diffBlocks[0].diffs[0];
      const tradeoffBlocks = diff?.__typename !== "OthersDiff" ? diff.tradeoffBlocks : undefined;
      setProposalStatus(data.proposal.state);
      setTradeoffBlocks(tradeoffBlocks);
    }
  }, [data]);

  // Poll rapidly when we're still loading suggestions.
  useEffect(() => {
    const pollInterval = proposalState === ProposalState.LoadingSuggestions ? 500 : 0;
    setPollInterval(pollInterval);
  }, [proposalState]);

  useEffect(() => {
    const timeRange = Interval.fromDateTimes(startTime, endTime).toISO();
    const attendees = inputAttendees.map(({ person, isOptional }) => ({
      email: person.email,
      optional: isOptional,
      isOrganizer: person.isMe,
    }));

    // We can only make a proposal if we have attendees
    if (!attendees.length) {
      return;
    }

    // If we don't have permission to modify, don't do anything
    if (!canModify) {
      return;
    }

    // If we're still creating the proposal, don't do anything
    if (createProposalLoading || createProposalFromEventLoading) {
      return;
    }

    // Something failed when creating the proposal, so don't try again
    if (createProposalError || createProposalFromEventError) {
      logger.error("Failed to create proposal", {
        createProposalError,
        createProposalFromEventError,
      });
      return;
    }

    // If the user is dragging to create a new event, don't do anything
    if (meta.isDragging) {
      return;
    }

    if (!localProposalId && eventId) {
      void createProposalFromEvent({
        variables: {
          eventId,
          timeRange,
          timeZone: startTime.zone.name,
        },
      });
    } else if (!localProposalId) {
      void createProposal({
        variables: {
          attendees: attendees.map(({ email, optional, isOrganizer }) => ({
            email,
            optional: optional ?? false,
            isOrganizer,
          })),
          timeRange,
          timeZone: getRenderTimeZone(),
          title: title,
        },
      });
    } else {
      void updateProposal({
        variables: {
          id: localProposalId,
          title,
          timeRange,
          timeZone: startTime.zone.name,
          attendees: attendees.map(({ email, optional, isOrganizer }) => ({
            email,
            optional: optional ?? false,
            isOrganizer,
          })),
          description,
          recurrenceRule: recurrenceRule?.toString(),
          conferenceType,
          location,
          flexDetails,
        },
      });
    }
  }, [
    eventId,
    canModify,
    inputAttendees,
    startTime,
    endTime,
    description,
    location,
    title,
    recurrenceRule,
    conferenceType,
    flexDetails,
    localProposalId,
    createProposal,
    createProposalFromEvent,
    updateProposal,
    createProposalLoading,
    createProposalFromEventLoading,
    meta.isDragging,
    createProposalError,
    createProposalFromEventError,
  ]);

  const providerValue = useMemo(
    () => ({
      id: localProposalId,
      status: proposalStatus,
      tradeoffBlocks,
    }),
    [localProposalId, proposalStatus, tradeoffBlocks],
  );

  const handleClearPersistedProposal = () => {
    setLocalProposalId(null);
    setProposalStatus(ProposalState.LoadingSuggestions);
    setTradeoffBlocks(undefined);
  };

  return (
    <WriteContext.Provider value={{ clearPersistedProposal: handleClearPersistedProposal }}>
      <ReadContext.Provider value={providerValue}>{children}</ReadContext.Provider>
    </WriteContext.Provider>
  );
};

export const usePersistedProposal = () => {
  return useContext(ReadContext);
};

export const useUpdatePersistedProposal = () => {
  const context = useContext(WriteContext);
  invariant(context, "useUpdatePersistedProposal must be within PersistedProposalProvider");
  return context;
};
